import React, { useState, useEffect, useRef } from 'react';
import './Verify.scss';
import { useParams, Navigate, Link, useLocation } from 'react-router-dom';
import { resendOtp, verifyOtp } from '../../api/messaging';
import { setPreferredNotifyChannel } from '../../utils/notifyChannel';
import { useNavigate } from "react-router-dom";
import { useProfile } from '../../Contexts/ProfileContext';
import { clearGuestData } from '../../utils/storage';
import {
    OTP_RESEND_COOLDOWN_MS,
    getOtpResendStorageKey,
    getOtpResendTimeLeftMs,
    getRetryAfterSeconds,
    isOtpCooldownError,
    markOtpResendCooldown,
} from '../otpCooldown';

// Mask identifier for display: phone ******5078, email u***@example.com
function maskIdentifier(identifier, type) {
    if (!identifier) return '';
    if (type === 'phone' || type === 'whatsapp') {
        const s = String(identifier).replace(/\D/g, '');
        return s.length >= 4 ? '******' + s.slice(-4) : '****';
    }
    // email: show first char + *** + @domain
    const [local, domain] = String(identifier).split('@');
    if (!domain) return '***';
    const masked = local?.charAt(0) + '***';
    return `${masked}@${domain}`;
}

export function Verify() {
    const { type = 'phone', identifier } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { fetchProfile } = useProfile();
    const isEmail = type === 'email';
    const isWhatsApp = type === 'whatsapp';
    const otpPurpose = location.state?.authFlow === 'register' ? 'register' : 'login';
    const decodedIdentifier = identifier ? decodeURIComponent(identifier) : '';

    const [resendTime, setResendTime] = useState(0);
    const [responseMessage, setResponseMessage] = useState('');
    const [infoMessage, setInfoMessage] = useState('');
    const inputRefs = useRef([]);
    const [restartTime, setRestartTime] = useState(0);
    const [disableButton, setDisableButton] = useState(false);
    const timerRef = useRef(null);

    const [value, setValue] = useState('');
    const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);

    const verifyRef = useRef(null);

    const resendCooldownMs = OTP_RESEND_COOLDOWN_MS;
    const resendStorageKey = getOtpResendStorageKey({
        purpose: otpPurpose,
        type,
        identifier,
    });

    const startResendTicker = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }
        timerRef.current = setInterval(() => {
            const left = getOtpResendTimeLeftMs(resendStorageKey);
            if (left <= 0) {
                clearInterval(timerRef.current);
                setResendTime(0);
                setInfoMessage('');
            } else {
                setResendTime(left);
            }
        }, 1000);
    };

    useEffect(() => {
        const fromStateSeconds = Number(location.state?.retryAfterSeconds);
        let timeLeft = getOtpResendTimeLeftMs(resendStorageKey);

        if (Number.isFinite(fromStateSeconds) && fromStateSeconds > 0) {
            markOtpResendCooldown(resendStorageKey, fromStateSeconds * 1000);
            timeLeft = getOtpResendTimeLeftMs(resendStorageKey);
        } else if (timeLeft <= 0 && location.state?.otpAlreadySent) {
            // Fresh send just before navigate — start full cooldown from now.
            markOtpResendCooldown(resendStorageKey, resendCooldownMs);
            timeLeft = resendCooldownMs;
        }

        setResendTime(timeLeft > 0 ? timeLeft : 0);

        if (location.state?.otpStillActive && timeLeft > 0) {
            setInfoMessage('A previous verification code is still active. Enter it below.');
        }

        if (timeLeft > 0) {
            startResendTicker();
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [resendCooldownMs, resendStorageKey, location.state?.retryAfterSeconds, location.state?.otpAlreadySent, location.state?.otpStillActive]);

    const phoneDigits = decodedIdentifier.replace(/\D/g, '').slice(-10);
    const normalizedPhone = phoneDigits.length === 10 ? phoneDigits : decodedIdentifier;

    const buildResendPayload = () => {
        if (isEmail) return { email: decodedIdentifier, purpose: otpPurpose };
        if (isWhatsApp) {
            return { phone: normalizedPhone, channel: 'whatsapp', purpose: otpPurpose };
        }
        return { phone: normalizedPhone, purpose: otpPurpose };
    };

    const buildVerifyPayload = () => {
        if (isEmail) {
            return {
                email: decodedIdentifier,
                otp: +value,
                type: 'verifyEmail',
                channel: 'email',
            };
        }
        return {
            phone: normalizedPhone,
            otp: +value,
            type: 'verifyPhone',
            channel: isWhatsApp ? 'whatsapp' : 'sms',
        };
    };

    // Request OTP when the verify page loads (skip if a code was already sent)
    useEffect(() => {
        if (!decodedIdentifier) return;
        if (location.state?.otpAlreadySent) return;
        if (getOtpResendTimeLeftMs(resendStorageKey) > 0) return;

        let cancelled = false;

        (async () => {
            try {
                setResponseMessage('');
                const response = await resendOtp(buildResendPayload());
                if (cancelled) return;
                const cooldownSec = response?.data?.cooldownSeconds;
                const durationMs =
                    Number.isFinite(Number(cooldownSec)) && Number(cooldownSec) > 0
                        ? Number(cooldownSec) * 1000
                        : resendCooldownMs;
                markOtpResendCooldown(resendStorageKey, durationMs);
                setResendTime(durationMs);
                startResendTicker();
                setInfoMessage('');
            } catch (error) {
                if (cancelled) return;
                if (isOtpCooldownError(error)) {
                    const retryAfter =
                        getRetryAfterSeconds(error) ?? Math.ceil(resendCooldownMs / 1000);
                    markOtpResendCooldown(resendStorageKey, retryAfter * 1000);
                    setResendTime(retryAfter * 1000);
                    startResendTicker();
                    setInfoMessage('A previous verification code is still active. Enter it below.');
                    setResponseMessage('');
                    return;
                }
                const msg = error.response?.data?.msg || error.response?.data?.message;
                const fallback = isWhatsApp
                    ? 'Could not send WhatsApp OTP. Join the Twilio sandbox first, then try again.'
                    : 'Could not send OTP. Check backend logs [OTP/Messaging].';
                setResponseMessage(msg || fallback);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [decodedIdentifier, isEmail, isWhatsApp, type, identifier, resendCooldownMs, resendStorageKey, location.state?.otpAlreadySent]);

    useEffect(() => {
        if (restartTime == 1) {
            markOtpResendCooldown(resendStorageKey, resendCooldownMs);
            setResendTime(resendCooldownMs);
            startResendTicker();
            setInfoMessage('');
            setRestartTime(0);
        }
    }, [restartTime, resendCooldownMs, resendStorageKey]);

    const formatTime = (milliseconds) => {
        const seconds = Math.floor(milliseconds / 1000);
        const minutes = Math.floor(seconds / 60);
        const displaySeconds = seconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${displaySeconds.toString().padStart(2, '0')}`;
    };

    const handleOtpChange = (index, value) => {
        // Allow only digits
        if (!/^\d*$/.test(value)) return;

        const newOtpValues = [...otpValues];
        newOtpValues[index] = value;
        setOtpValues(newOtpValues);

        // Auto-focus to next input
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }

        // Update the main value for API call
        setValue(newOtpValues.join(''));
    };

    const handleKeyDown = (index, e) => {
        // Handle backspace
        if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        
        // Handle Enter key
        if (e.key === 'Enter') {
            document.querySelector('.verify')?.click();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        const newOtpValues = pastedData.split('').concat(Array(6 - pastedData.length).fill(''));
        setOtpValues(newOtpValues);
        setValue(pastedData);
        
        // Focus on the next empty field or last field
        const nextIndex = Math.min(pastedData.length, 5);
        inputRefs.current[nextIndex]?.focus();
    };

    // Handler for form submission
    const handleSubmit = async (event) => {
        event.preventDefault();

        const otpComplete = /^\d{6}$/.test(value);

        try {
            setDisableButton(true);

            // Button label is Resend when timer is 0 — always resend in that state,
            // even if digits are still filled (max-attempts left OTP in the inputs).
            if (resendTime <= 0) {
                setResponseMessage('');
                setInfoMessage('');
                try {
                    const response = await resendOtp(buildResendPayload());
                    const cooldownSec = response?.data?.cooldownSeconds;
                    const durationMs =
                        Number.isFinite(Number(cooldownSec)) && Number(cooldownSec) > 0
                            ? Number(cooldownSec) * 1000
                            : resendCooldownMs;
                    markOtpResendCooldown(resendStorageKey, durationMs);
                    setValue('');
                    setOtpValues(['', '', '', '', '', '']);
                    setRestartTime(1);
                    setTimeout(() => inputRefs.current[0]?.focus(), 0);
                } catch (resendError) {
                    if (isOtpCooldownError(resendError)) {
                        const retryAfter =
                            getRetryAfterSeconds(resendError) ?? Math.ceil(resendCooldownMs / 1000);
                        markOtpResendCooldown(resendStorageKey, retryAfter * 1000);
                        setResendTime(retryAfter * 1000);
                        startResendTicker();
                        setInfoMessage('A previous verification code is still active. Enter it below.');
                        setResponseMessage('');
                    } else {
                        throw resendError;
                    }
                }
                setDisableButton(false);
            } else if (!otpComplete) {
                setResponseMessage('Please enter the 6-digit code.');
                setDisableButton(false);
            } else {
                const verifyPayload = buildVerifyPayload();
                const response = await verifyOtp(verifyPayload);
                if (verifyPayload.channel) {
                    setPreferredNotifyChannel(verifyPayload.channel);
                }

                const token = response?.data?.token;
                const passwordSet = response?.data?.passwordSet;
                if (token) {
                    clearGuestData();
                    localStorage.setItem('token', token);
                    await fetchProfile(true);
                }
                if (passwordSet === true) {
                    localStorage.setItem('passwordSet', 'true');
                    navigate('/');
                } else {
                    navigate('/auth/set-password');
                }
                setDisableButton(false);
            }
        } catch (error) {
            const msg = error.response?.data?.msg || (resendTime <= 0 ? 'Could not resend code.' : 'Invalid OTP');
            const triesLeft = error.response?.data?.data?.triesLeft;
            setResponseMessage(msg);
            // After max attempts, clear stale digits so the next Resend click is obvious.
            if (triesLeft === 0 || /maximum attempts/i.test(String(msg))) {
                setValue('');
                setOtpValues(['', '', '', '', '', '']);
            }
            setDisableButton(false);
        } finally {
            //   setLoading(false); // Set loading to false after the API call is complete
        }
    };

    if (!identifier || !decodedIdentifier) {
        return <Navigate to="/" replace />;
    }

    return (
        <div className="web-reactPageContent">
            <div className="verificationContainer">
                <div className="otpTopImage">
                    <div className="image">
                        <div
                            className="LazyLoad is-visible"
                            style={{ height: 'auto', width: '100%' }}
                        >
                            <picture className="img-responsive" style={{ width: '100%' }}>
                                <source
                                    srcSet="//constant.myntassets.com/pwa/assets/img/3a438cb4-c9bf-4316-b60c-c63e40a1a96d1548071106233-mobile-verification.jpg"
                                    type="image/webp"
                                />
                                <img
                                    src="//constant.myntassets.com/pwa/assets/img/3a438cb4-c9bf-4316-b60c-c63e40a1a96d1548071106233-mobile-verification.jpg"
                                    className="img-responsive preLoad loaded"
                                    alt=""
                                    title=""
                                    style={{ width: '100%' }}
                                />
                            </picture>
                        </div>
                    </div>
                </div>
                <div className="mobContainer">
                    <h3>
                        {isEmail ? 'Verify with email code' : isWhatsApp ? 'Verify with WhatsApp OTP' : 'Verify with OTP'}
                    </h3>
                    <h4>
                        {isWhatsApp ? 'Sent to WhatsApp ' : 'Sent to '}
                        {maskIdentifier(decodedIdentifier, type)}
                    </h4>
                    <div className="otpContainer">
                        {otpValues.map((digit, index) => (
                            <input
                                key={index}
                                ref={(el) => (inputRefs.current[index] = el)}
                                type="tel"
                                maxLength="1"
                                className="otpInput"
                                value={digit}
                                onChange={(e) => handleOtpChange(index, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(index, e)}
                                onPaste={handlePaste}
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                autoCorrect="off"
                                autoCapitalize="off"
                                spellCheck={false}
                            />
                        ))}
                    </div>
                    {infoMessage && (
                        <div className="responseMessage responseMessage--info">
                            <i className="bi bi-info-circle-fill"></i>
                            <span>{infoMessage}</span>
                        </div>
                    )}
                    {responseMessage && (
                        <div className="responseMessage">
                            <i className="bi bi-exclamation-circle-fill"></i>
                            <span>{responseMessage}</span>
                        </div>
                    )}
                    <div>
                        {resendTime > 0 && (
                        <div className="resendTime">
                            <span>
                                {isEmail ? 'Resend code in: ' : isWhatsApp ? 'Resend WhatsApp OTP in: ' : 'Resend OTP in: '}
                            </span>{' '}
                            {formatTime(resendTime)}
                        </div>
                        )}
                    </div>

                    <div
                        className='verify'
                        ref={verifyRef}
                        disabled={disableButton}
                        onClick={(e) => handleSubmit(e)}
                    >
                        {resendTime <= 0
                            ? (isEmail ? 'Resend code' : isWhatsApp ? 'Resend WhatsApp OTP' : 'Resend OTP')
                            : 'Verify'}
                    </div>

                    <div className="bottomeLink">
                        {" "}
                        Log in using <Link to="/auth/login">Password</Link>{" "}
                    </div>
                    <div className="bottomeLink">
                        {" "}
                        Having trouble logging in? <span> Get help </span>{" "}
                    </div>
                </div>
            </div>
        </div>
    );
}
